import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import authFetch from '../../utils/authFetch';
import { 
    DocumentTextIcon, 
    EyeIcon, 
    SparklesIcon,
    SwatchIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import TemplateSelector from './TemplateSelector';
import CvEditor from './CvEditor';
import CvPreview from './CvPreview';
import AbTesting from './AbTesting';
import AiAssistantButton from '../AiAssistantButton';
import AiTips from '../AiTips';
import { trackCvGeneration, trackCvExport, trackAbTest } from '../../utils/analytics';
import { exportCvToPdf, downloadBlob } from '../../utils/pdfExport';
import { useToast } from '../ToastProvider';

const CvGenerator = () => {
    const { success, error: toastError, info } = useToast();
    const autosaveTimerRef = useRef(null);
    const [currentStep, setCurrentStep] = useState('template'); // template, editor, preview
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [cvData, setCvData] = useState({
        personalInfo: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            linkedin: '',
            website: ''
        },
        summary: '',
        experience: [],
        education: [],
        skills: [],
        languages: [],
        certifications: [],
        projects: []
    });
    const [cvStyle, setCvStyle] = useState({
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af',
        fontFamily: 'Inter',
        fontSize: 'medium',
        spacing: 'normal'
    });
    const [abTestData, setAbTestData] = useState({
        versionA: null,
        versionB: null,
        isActive: false,
        results: {}
    });
    const [isLoading, setIsLoading] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [linkedinCode, setLinkedinCode] = useState(null);

    const token = useSelector((state) => state.auth.token);
    const userId = useSelector((state) => state.auth.user.id);

    const templates = [
        {
            id: 'modern-blue',
            name: 'Moderne Bleu',
            preview: '/templates/modern-blue.png',
            colors: { primary: '#2563eb', secondary: '#1e40af' },
            font: 'Inter',
            category: 'moderne'
        },
        {
            id: 'elegant-dark',
            name: 'Élégant Sombre',
            preview: '/templates/elegant-dark.png',
            colors: { primary: '#1f2937', secondary: '#374151' },
            font: 'Poppins',
            category: 'élégant'
        },
        {
            id: 'creative-gradient',
            name: 'Créatif Dégradé',
            preview: '/templates/creative-gradient.png',
            colors: { primary: '#8b5cf6', secondary: '#a855f7' },
            font: 'Montserrat',
            category: 'créatif'
        },
        {
            id: 'minimal-clean',
            name: 'Minimal Propre',
            preview: '/templates/minimal-clean.png',
            colors: { primary: '#059669', secondary: '#047857' },
            font: 'Roboto',
            category: 'minimal'
        },
        {
            id: 'professional-gray',
            name: 'Professionnel Gris',
            preview: '/templates/professional-gray.png',
            colors: { primary: '#6b7280', secondary: '#4b5563' },
            font: 'Open Sans',
            category: 'professionnel'
        },
        {
            id: 'bold-red',
            name: 'Audacieux Rouge',
            preview: '/templates/bold-red.png',
            colors: { primary: '#dc2626', secondary: '#b91c1c' },
            font: 'Bebas Neue',
            category: 'audacieux'
        }
    ];

    // État pour éviter les rechargements multiples
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        // Éviter les rechargements multiples
        if (hasInitialized) {
            return;
        }
        setHasInitialized(true);

        // Charger les données existantes si disponibles
        loadExistingCvData();

        // Vérifier s'il y a un code LinkedIn dans l'URL
        const urlParams = new URLSearchParams(window.location.search);
        const linkedinCode = urlParams.get('code');
        const linkedinError = urlParams.get('linkedin_error');
        
        if (linkedinCode) {
            // Nettoyer l'URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // Stocker le code LinkedIn pour le passer au TemplateSelector
            setLinkedinCode(linkedinCode);
        } else if (linkedinError) {
            toastError(decodeURIComponent(linkedinError));
            // Nettoyer l'URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Tenter de restaurer un brouillon local si présent
        try {
            const draftKey = `cv_draft_${userId || 'guest'}`;
            const draft = localStorage.getItem(draftKey);
            if (draft) {
                const { cvData: dData, cvStyle: dStyle, template: dTemplate } = JSON.parse(draft);
                if (dData) {
                    setCvData(dData);
                }
                if (dStyle) {
                    setCvStyle(dStyle);
                }
                if (dTemplate) {
                    setSelectedTemplate(dTemplate);
                }
                
                // Si on a des données, passer directement à l'éditeur
                if (dData && dTemplate) {
                    setCurrentStep('editor');
                }
                
                info('Brouillon local restauré');
            }
        } catch (error) {
            // Erreur silencieuse pour la restauration du brouillon
        }

        // Avertir en cas de navigation si modifications non enregistrées
        const beforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasInitialized]); // Dépendance uniquement sur hasInitialized

    // Référence pour éviter les appels multiples
    const loadDataRef = useRef(false);

    const loadExistingCvData = async () => {
        // Éviter les appels multiples
        if (loadDataRef.current) {
            return;
        }

        loadDataRef.current = true;
        
        try {
            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/user-data/${userId}`);
            
            if (response.ok) {
                const data = await response.json();
                if (data.cvData) {
                    setCvData(data.cvData);
                    if (data.cvStyle) setCvStyle(data.cvStyle);
                    if (data.template) setSelectedTemplate(data.template);
                    
                    // Si on a des données du serveur, passer directement à l'éditeur
                    setCurrentStep('editor');
                    
                    success('Données CV restaurées');
                }
            }
        } catch (error) {
            // Erreur silencieuse pour le chargement des données
        } finally {
            // Réinitialiser après un délai pour permettre les futurs chargements
            setTimeout(() => {
                loadDataRef.current = false;
            }, 1000);
        }
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setCvStyle(prev => ({
            ...prev,
            primaryColor: template.colors.primary,
            secondaryColor: template.colors.secondary,
            fontFamily: template.font
        }));
        setCurrentStep('editor');
        
        // Tracking
        trackCvGeneration('template_selected', template.id);
    };

    const handleLinkedInImport = (linkedinData) => {
        // Fusionner les données LinkedIn avec les données existantes
        const mergedData = {
            ...cvData,
            ...linkedinData
        };
        
        setCvData(mergedData);
        
        // Passer à l'éditeur avec les données importées
        setCurrentStep('editor');
        
        // Tracking
        trackCvGeneration('linkedin_import', 'success');
        
        success('Données LinkedIn importées avec succès !');
    };

    const handleCvDataUpdate = (newData) => {
        setCvData(newData);
        triggerAutosave(newData, cvStyle, selectedTemplate);
    };

    const handleStyleUpdate = (newStyle) => {
        setCvStyle(newStyle);
        triggerAutosave(cvData, newStyle, selectedTemplate);
    };

    const handlePreview = () => {
        setCurrentStep('preview');
    };

    const triggerAutosave = (data, style, template) => {
        setHasUnsavedChanges(true);
        const draftKey = `cv_draft_${userId || 'guest'}`;
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            try {
                localStorage.setItem(
                    draftKey,
                    JSON.stringify({ cvData: data, cvStyle: style, template })
                );
                info('Brouillon sauvegardé');
            } catch {}
        }, 1500);
    };

    const handleAbTest = () => {
        // Générer une version B alternative
        const generateVersionB = () => {
            // Créer une version alternative avec des modifications
            const alternativeStyle = {
                ...cvStyle,
                primaryColor: '#8b5cf6', // Couleur violette alternative
                secondaryColor: '#a855f7',
                fontFamily: cvStyle.fontFamily === 'Inter' ? 'Poppins' : 'Inter'
            };

            // Créer des données alternatives (exemple : réorganiser les sections)
            const alternativeData = {
                ...cvData,
                // Réorganiser l'ordre des expériences si il y en a plusieurs
                experience: cvData.experience.length > 1 
                    ? [...cvData.experience.slice(1), cvData.experience[0]] 
                    : cvData.experience,
                // Modifier légèrement le résumé
                summary: cvData.summary 
                    ? cvData.summary.replace(/^/, '🎯 ') // Ajouter un emoji au début
                    : cvData.summary
            };

            return {
                cvData: alternativeData,
                cvStyle: alternativeStyle,
                template: {
                    ...selectedTemplate,
                    name: `${selectedTemplate?.name} (Alternative)`,
                    colors: { 
                        primary: alternativeStyle.primaryColor, 
                        secondary: alternativeStyle.secondaryColor 
                    }
                }
            };
        };

        const versionB = generateVersionB();

        setAbTestData(prev => ({
            ...prev,
            versionA: { cvData, cvStyle, template: selectedTemplate },
            versionB: versionB,
            isActive: true
        }));
        setCurrentStep('ab-test');
        
        // Tracking
        trackAbTest('started');
    };

    const handleExport = async (format = 'pdf') => {
        setIsLoading(true);
        try {
            if (format === 'pdf') {
                // Utiliser la nouvelle fonction d'export PDF qui gère les deux cas
                const blob = await exportCvToPdf(cvData, cvStyle, selectedTemplate, token);
                if (blob) {
                    const filename = `CV_${cvData.personalInfo.firstName}_${cvData.personalInfo.lastName}.pdf`;
                    downloadBlob(blob, filename);
                }
                // Tracking
                trackCvExport('pdf', selectedTemplate.id);
                success('PDF exporté avec succès');
            } else {
                // Export Word - reste inchangé
                const exportData = {
                    cvData,
                    cvStyle,
                    template: selectedTemplate,
                    format
                };

                const response = await authFetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/export-word-from-data', {
                    method: 'POST',
                    body: JSON.stringify(exportData)
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `CV_${cvData.personalInfo.firstName}_${cvData.personalInfo.lastName}.docx`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    
                    // Tracking
                    trackCvExport('docx', selectedTemplate.id);
                    success('Fichier Word exporté avec succès');
                } else {
                    throw new Error(`Erreur ${response.status}: ${response.statusText}`);
                }
            }
        } catch (error) {
            toastError("Erreur lors de l'export du CV. Veuillez réessayer.");
        } finally {
            setIsLoading(false);
        }
    };

    const saveCvData = async () => {
        try {
            const saveData = {
                userId,
                cvData,
                cvStyle,
                template: selectedTemplate
            };
            
            const response = await authFetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/save-data', {
                method: 'POST',
                body: JSON.stringify(saveData)
            });
            
            if (response.ok) {
                success('CV enregistré avec succès');
                // Clear draft après enregistrement
                try {
                    const draftKey = `cv_draft_${userId || 'guest'}`;
                    localStorage.removeItem(draftKey);
                } catch {}
                setHasUnsavedChanges(false);
            } else {
                throw new Error(`Erreur ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            toastError('Erreur lors de la sauvegarde');
        }
    };


    const handleBackToEditor = () => {
        setCurrentStep('editor');
    };

    const handleNavigateToSection = (sectionId) => {
        // S'assurer qu'on est dans l'éditeur
        if (currentStep !== 'editor') {
            setCurrentStep('editor');
        }
        
        // Attendre que l'éditeur soit rendu puis naviguer
        setTimeout(() => {
            // Émettre un événement personnalisé pour que l'éditeur puisse réagir
            window.dispatchEvent(new CustomEvent('navigateToSection', { 
                detail: { sectionId } 
            }));
        }, 100);
        
        success(`Navigation vers la section ${sectionId}`);
    };

    const handleChangeTemplate = () => {
        // Sauvegarder les données actuelles avant de changer
        triggerAutosave(cvData, cvStyle, selectedTemplate);
        
        // Retourner à la sélection de template
        setCurrentStep('template');
        
        info('Vos données ont été sauvegardées. Vous pouvez changer de template.');
    };

    const renderStep = () => {
        switch (currentStep) {
            case 'template':
                return (
                    <TemplateSelector 
                        templates={templates}
                        onSelect={handleTemplateSelect}
                        onLinkedInImport={handleLinkedInImport}
                        linkedinCode={linkedinCode} // Passer le code LinkedIn au TemplateSelector
                    />
                );
            case 'editor':
                return (
                    <CvEditor
                        cvData={cvData}
                        cvStyle={cvStyle}
                        template={selectedTemplate}
                        onDataUpdate={handleCvDataUpdate}
                        onStyleUpdate={handleStyleUpdate}
                        onPreview={handlePreview}
                        onAbTest={handleAbTest}
                        onSave={saveCvData}
                        onChangeTemplate={handleChangeTemplate}
                    />
                );
            case 'preview':
                return (
                    <CvPreview
                        cvData={cvData}
                        cvStyle={cvStyle}
                        template={selectedTemplate}
                        onBack={handleBackToEditor}
                        onExport={handleExport}
                        isLoading={isLoading}
                    />
                );
            case 'ab-test':
                return (
                    <AbTesting
                        versionA={abTestData.versionA}
                        versionB={abTestData.versionB}
                        onBack={handleBackToEditor}
                        onExport={handleExport}
                        isLoading={isLoading}
                    />
                );
            default:
                return null;
        }
    };

    const getStepIndicator = () => {
        const steps = [
            { key: 'template', label: 'Template', icon: DocumentTextIcon },
            { key: 'editor', label: 'Édition', icon: SwatchIcon },
            { key: 'preview', label: 'Aperçu', icon: EyeIcon },
        ];

        const currentStepIndex = steps.findIndex(step => step.key === currentStep);

        return (
            <div className="flex items-center justify-center mb-8">
                <div className="flex items-center space-x-4">
                    {steps.map((step, index) => {
                        const isActive = step.key === currentStep;
                        const isCompleted = index < currentStepIndex;
                        
                        return (
                            <div key={step.key} className="flex items-center">
                                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                                    isActive 
                                        ? 'bg-blue-600 border-blue-600 text-white' 
                                        : isCompleted 
                                            ? 'bg-green-500 border-green-500 text-white'
                                            : 'bg-gray-100 border-gray-300 text-gray-500'
                                }`}>
                                    {isCompleted ? (
                                        <CheckCircleIcon className="w-5 h-5" />
                                    ) : (
                                        <step.icon className="w-5 h-5" />
                                    )}
                                </div>
                                <span className={`ml-2 text-sm font-medium ${
                                    isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                                }`}>
                                    {step.label}
                                </span>
                                {index < steps.length - 1 && (
                                    <div className={`w-8 h-0.5 mx-2 ${
                                        isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                    }`} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 pt-20 p-4 overflow-x-hidden">
            <div className="max-w-7xl mx-auto min-w-0">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <SparklesIcon className="w-8 h-8 text-blue-600 mr-3" />
                        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            Générateur de CV
                        </h1>
                    </div>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto break-words">
                        Créez un CV professionnel avec des templates modernes et testez différentes versions
                    </p>
                </div>

                {/* Step Indicator */}
                {getStepIndicator()}

                {/* Main Content */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden max-w-full min-w-0">
                    {renderStep()}
                </div>

                                 {/* Conseils IA */}
                 {currentStep === 'editor' && (
                     <div className="mt-6">
                         <AiTips 
                             cvData={cvData} 
                             isVisible={true}
                             onClose={() => {}} // Optionnel : permettre de fermer
                             onNavigateToSection={handleNavigateToSection}
                         />
                     </div>
                 )}

                {/* Assistant IA */}
                <AiAssistantButton cvData={cvData} />
            </div>
        </div>
    );
};

export default CvGenerator; 